import type { AutocompleteSuggestion, CountryCode } from '@gql/graphql';
import { useCallback, useEffect, useState } from 'react';
import { useRouter } from '../useRouter';
import { useGetAutocompleteResults } from './useGetAutocompleteSuggestions';

export interface UsePlaceParams {
  placeId?: string;
  input?: string;
}

export const usePlace = (params?: UsePlaceParams) => {
  const { language } = useRouter();

  const { getAutocompleteSuggestions, suggestionsError, suggestionsLoading } =
    useGetAutocompleteResults();

  const getAutocompletePredictions = useCallback(
    async (options: {
      search?: string;
      country?: CountryCode;
    }): Promise<AutocompleteSuggestion[] | undefined> => {
      const { country, search } = options;
      if (!search || typeof search !== 'string') return;
      const response = await getAutocompleteSuggestions({
        language,
        country,
        location: search,
      });
      return response?.data?.autocompleteSuggestions as AutocompleteSuggestion[] | undefined;
    },
    [getAutocompleteSuggestions, language],
  );

  const getPlace = useCallback(
    async (getParams?: UsePlaceParams) => {
      const places = await getAutocompletePredictions({ search: getParams?.input });
      if (places?.length) {
        let id = getParams?.placeId;
        if (getParams?.input) {
          let found = places?.find((x) => x.label === getParams.input);
          if (!found) {
            [found] = places;
          }
          id = found?.placeId;
        }
        if (id) {
          return places?.find((option) => option.placeId === id);
        }
      }
    },
    [getAutocompletePredictions],
  );

  const [place, setPlace] = useState<AutocompleteSuggestion | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const foundPlace = await getPlace(params);
      setPlace(foundPlace ?? undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPlace, params?.input, params?.placeId]);

  return {
    suggestionsError,
    suggestionsLoading,
    place,
    getPlace,
    getAutocompletePredictions,
  };
};
